const STAGE_CONFIG = {
  lead: { label: 'Lead', color: '#717680' },
  qualified: { label: 'Qualified', color: '#2E90FA' },
  proposal: { label: 'Proposal', color: '#F79009' },
  negotiation: { label: 'Negotiation', color: '#A2DC47' },
  identification: { label: 'Identification', color: '#717680' },
  qualification: { label: 'Qualification', color: '#2E90FA' },
  cultivation: { label: 'Cultivation', color: '#F79009' },
  solicitation: { label: 'Solicitation', color: '#A2DC47' },
  stewardship: { label: 'Stewardship', color: '#7C3AED' },
  closed_won: { label: 'Closed Won', color: '#12B76A' },
  closed_lost: { label: 'Closed Lost', color: '#F04438' },
  not_a_prospect: { label: 'Not a prospect', color: '#717680' },
};

export const OPEN_STAGES_BY_CATEGORY = {
  sales: ['lead', 'qualified', 'proposal', 'negotiation'],
  fundraising: ['identification', 'qualification', 'cultivation', 'solicitation'],
};

export function openStagesFor(category) {
  return OPEN_STAGES_BY_CATEGORY[category] || OPEN_STAGES_BY_CATEGORY.sales;
}

export function getStageBadgeConfig(stage) {
  const value = stage || 'unknown';
  return STAGE_CONFIG[value] || { label: value.replace('_', ' '), color: '#717680' };
}

export function getStageLabel(stage) {
  return getStageBadgeConfig(stage).label;
}

const STAGE_BADGE_COLORS = {
  lead: 'gray',
  identification: 'gray',
  qualified: 'blue',
  qualification: 'blue',
  proposal: 'orange',
  cultivation: 'orange',
  negotiation: 'success',
  solicitation: 'success',
  stewardship: 'purple',
  closed_won: 'success',
  closed_lost: 'error',
  not_a_prospect: 'gray',
};

export function getStageBadgeColor(stage) {
  return STAGE_BADGE_COLORS[stage] || 'gray';
}

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'blue' },
  won: { label: 'Won', color: 'success' },
  lost: { label: 'Lost', color: 'error' },
  deferred: { label: 'Deferred', color: 'warning' },
};

export function getStatusConfig(status) {
  if (!status) return { label: '—', color: 'gray' };
  return STATUS_CONFIG[status] || { label: status, color: 'gray' };
}

export function statusOptions(statuses = Object.keys(STATUS_CONFIG)) {
  return statuses.map((status) => ({ value: status, label: getStatusConfig(status).label }));
}

export function formatProbability(probability) {
  if (probability == null) return '—';
  return `${probability}%`;
}

export function getTypeBadgeColor(dealType) {
  if (dealType === 'fundraising') return 'purple';
  if (dealType === 'sales') return 'blue';
  return 'gray';
}

export function initialsFor(name) {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

export function getContactInitials(contact) {
  if (!contact) return '?';
  return initialsFor(contact.name) || '?';
}

const TYPE_COLORS = {
  fundraising: '#7C3AED',
  sales: '#2E90FA',
};

export function getTypeLabel(dealType, types = []) {
  if (!dealType) return '—';
  const match = types.find((t) => t.value === dealType);
  return match ? match.label : dealType;
}

export function getTypeColor(dealType) {
  return TYPE_COLORS[dealType] || '#717680';
}

export function formatAmount(amount) {
  if (!amount && amount !== 0) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatCloseDate(closeDate) {
  if (!closeDate) return '—';
  const parts = closeDate.substring(0, 10).split('-');
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatActivity(timestamp) {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatRelativeActivity(timestamp, now = new Date()) {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '—';

  const startOfLocalDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfLocalDay(now) - startOfLocalDay(date)) / 86400000);

  if (days < 0) return formatActivity(timestamp);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 35) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  }
  return formatActivity(timestamp);
}
